import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import { alpha, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { TUploadedFile } from '@onesource/schemas';
import { useHotkey } from '@tanstack/react-hotkeys';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { MdChevronLeft, MdChevronRight, MdClose, MdContentCopy, MdInfoOutline } from 'react-icons/md';

import { CopyUrlMenu } from './copy-url-menu';
import { MediaFileDetails } from './media-file-details';

type TMediaPreviewDialogProps = {
  file: TUploadedFile | null;
  onClose: () => void;
  files?: TUploadedFile[];
  onNavigate?: (file: TUploadedFile) => void;
};

export function MediaPreviewDialog({ file, onClose, files, onNavigate }: TMediaPreviewDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:599px)');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const ext = file?.filename.split('.').pop()?.toUpperCase() ?? '';
  const isPdf = file?.filename.toLowerCase().endsWith('.pdf') ?? false;

  const currentIndex = files && file ? files.findIndex(f => f.key === file.key) : -1;
  const hasPrev = currentIndex > 0;
  const hasNext = files ? currentIndex < files.length - 1 : false;

  const handlePrev = () => {
    if (hasPrev && files && onNavigate) {
      onNavigate(files[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (hasNext && files && onNavigate) {
      onNavigate(files[currentIndex + 1]);
    }
  };

  const isOpen = !!file;

  useHotkey('ArrowLeft', handlePrev, { enabled: isOpen && hasPrev });
  useHotkey('ArrowRight', handleNext, { enabled: isOpen && hasNext });
  useHotkey('Escape', onClose, { enabled: isOpen });

  const overlayBtnSx = {
    width: 40,
    height: 40,
    color: '#fff',
    bgcolor: alpha('#000', 0.45),
    '&:hover': { bgcolor: alpha('#000', 0.65) },
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
      slotProps={{ backdrop: { sx: { bgcolor: alpha('#000', 0.85) } } }}
      PaperProps={{
        sx: {
          m: { xs: 0, sm: 2 },
          display: 'flex',
          flexDirection: 'row',
          overflow: 'hidden',
          borderRadius: { xs: 0, sm: 2 },
          maxHeight: { xs: '100dvh', sm: 'calc(100dvh - 32px)' },
        },
      }}
    >
      {/* Image area */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme.palette.grey[900],
          minHeight: { xs: '50vh', sm: 400 },
          overflow: 'hidden',
        }}
      >
        {isPdf ? (
          <Box
            component="embed"
            src={file?.publicUrl ?? ''}
            type="application/pdf"
            sx={{ width: '100%', height: '88vh', display: 'block', border: 'none' }}
          />
        ) : (
          <Box
            component="img"
            src={file?.publicUrl ?? ''}
            alt={file?.filename ?? ''}
            sx={{ width: '100%', maxHeight: '88vh', objectFit: 'contain', display: 'block' }}
          />
        )}

        {ext && (
          <Chip
            label={ext}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              height: 22,
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: 0.5,
              bgcolor: 'background.paper',
              boxShadow: theme.shadows[1],
            }}
          />
        )}

        {/* Top-right overlay buttons: copy + close + info (mobile) */}
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            display: 'flex',
            gap: 0.75,
            zIndex: 2,
          }}
        >
          {/* Copy URL — always visible on mobile */}
          <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
            {file?.cdnUrls && Object.keys(file.cdnUrls).length > 0 ? (
              <CopyUrlMenu cdnUrls={file.cdnUrls} variantSizes={{}} standalone buttonSx={overlayBtnSx} iconSize={20} />
            ) : file?.publicUrl ? (
              <Tooltip title="Copy URL" placement="bottom" arrow>
                <IconButton
                  size="small"
                  sx={overlayBtnSx}
                  onClick={() => {
                    navigator.clipboard.writeText(file.publicUrl!);
                    enqueueSnackbar('URL copied to clipboard', { variant: 'success', autoHideDuration: 2000 });
                  }}
                >
                  <MdContentCopy size={20} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>

          {/* Info toggle — mobile only */}
          <Tooltip title="File details" placement="bottom" arrow>
            <IconButton
              size="small"
              sx={{ ...overlayBtnSx, display: { xs: 'inline-flex', sm: 'none' } }}
              onClick={() => setSidebarOpen(prev => !prev)}
            >
              <MdInfoOutline size={20} />
            </IconButton>
          </Tooltip>

          {/* Close — always visible */}
          <Tooltip title="Close" placement="bottom" arrow>
            <IconButton size="small" sx={overlayBtnSx} onClick={onClose}>
              <MdClose size={20} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Prev button */}
        {onNavigate && files && files.length > 1 && (
          <Box
            onClick={handlePrev}
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: hasPrev ? 'pointer' : 'default',
              bgcolor: hasPrev ? alpha('#000', 0.12) : 'transparent',
              transition: 'bgcolor 0.2s',
              '&:hover': hasPrev ? { bgcolor: alpha('#000', 0.22) } : {},
            }}
          >
            <IconButton
              disabled={!hasPrev}
              tabIndex={-1}
              sx={{
                color: '#fff',
                opacity: hasPrev ? 1 : 0.2,
                bgcolor: alpha('#000', 0.25),
                '&:hover': { bgcolor: alpha('#000', 0.4) },
                '&.Mui-disabled': { color: '#fff' },
              }}
            >
              <MdChevronLeft size={28} />
            </IconButton>
          </Box>
        )}

        {/* Next button */}
        {onNavigate && files && files.length > 1 && (
          <Box
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: hasNext ? 'pointer' : 'default',
              bgcolor: hasNext ? alpha('#000', 0.12) : 'transparent',
              transition: 'bgcolor 0.2s',
              '&:hover': hasNext ? { bgcolor: alpha('#000', 0.22) } : {},
            }}
          >
            <IconButton
              disabled={!hasNext}
              tabIndex={-1}
              sx={{
                color: '#fff',
                opacity: hasNext ? 1 : 0.2,
                bgcolor: alpha('#000', 0.25),
                '&:hover': { bgcolor: alpha('#000', 0.4) },
                '&.Mui-disabled': { color: '#fff' },
              }}
            >
              <MdChevronRight size={28} />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Details sidebar — always visible ≥600px, toggled on mobile */}
      <Box
        sx={{
          width: 300,
          flexShrink: 0,
          display: { xs: sidebarOpen ? 'flex' : 'none', sm: 'flex' },
          flexDirection: 'column',
          bgcolor: 'background.paper',
          position: { xs: 'absolute', sm: 'static' },
          top: { xs: 0 },
          right: { xs: 0 },
          bottom: { xs: 0 },
          zIndex: { xs: 3 },
          boxShadow: { xs: `-4px 0 16px ${alpha('#000', 0.2)}`, sm: 'none' },
        }}
      >
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />

        {/* Header */}
        <Box sx={{ px: 2, pt: 1.5, pb: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mb: 1 }}>
            {/* Copy — desktop only (mobile has it overlaid on image) */}
            <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
              {file?.cdnUrls && Object.keys(file.cdnUrls).length > 0 ? (
                <CopyUrlMenu cdnUrls={file.cdnUrls} variantSizes={{}} standalone />
              ) : file?.publicUrl ? (
                <Tooltip title="Copy URL" placement="bottom" arrow>
                  <IconButton
                    sx={{
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        color: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                    onClick={() => {
                      navigator.clipboard.writeText(file.publicUrl!);
                      enqueueSnackbar('URL copied to clipboard', { variant: 'success', autoHideDuration: 2000 });
                    }}
                  >
                    <MdContentCopy size={22} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>

            {/* Close — desktop only (mobile has it overlaid on image) */}
            <Tooltip title="Close" placement="bottom" arrow>
              <IconButton
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  color: theme.palette.text.secondary,
                  '&:hover': { color: theme.palette.error.main, bgcolor: alpha(theme.palette.error.main, 0.08) },
                }}
                onClick={onClose}
              >
                <MdClose size={22} />
              </IconButton>
            </Tooltip>

            {/* Dismiss sidebar — mobile only */}
            <Tooltip title="Hide details" placement="bottom" arrow>
              <IconButton
                sx={{
                  display: { xs: 'inline-flex', sm: 'none' },
                  color: theme.palette.text.secondary,
                  '&:hover': { color: theme.palette.error.main, bgcolor: alpha(theme.palette.error.main, 0.08) },
                }}
                onClick={() => setSidebarOpen(false)}
              >
                <MdClose size={22} />
              </IconButton>
            </Tooltip>
          </Box>

          <Typography variant="subtitle2" fontWeight={700} sx={{ wordBreak: 'break-all' }}>
            {file?.filename}
          </Typography>

          {files && files.length > 1 && currentIndex >= 0 && (
            <Typography variant="caption" color="text.secondary">
              {currentIndex + 1} / {files.length}
            </Typography>
          )}
        </Box>

        {/* Detail rows */}
        <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>{file && <MediaFileDetails file={file} />}</Box>
      </Box>
    </Dialog>
  );
}
